import React, {useState} from 'react';
import {useFormik} from 'formik';
import {AGCStaticSelect} from '../../generic-components/AGCStaticSelect';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

export function ActiveJobsTable({ jobs = [], loading = false, onJobClick }) {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
    const [searchTerm, setSearchTerm] = useState('');

    // Initialize Formik for filter management
    const formik = useFormik({
        initialValues: {
            filterBranch: null,
            filterStatus: null
        },
        onSubmit: () => {}
    });

    // Get unique branches for filter
    const branches = [...new Set(jobs.map(job => job.branchName).filter(Boolean))].sort();
    const branchOptions = branches.map(branch => ({id: branch, name: branch}));
    const statusOptions = [
        {id: 'OPEN', name: 'Open'},
        {id: 'IN_PROGRESS', name: 'In Progress'}
    ];

    // Filter and sort jobs (latest first)
    const filteredJobs = jobs.filter(job => {
        const matchesSearch = !searchTerm ||
            job.vehicleNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.roNumber?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesBranch = !formik.values.filterBranch || formik.values.filterBranch.id === '' || job.branchName === formik.values.filterBranch.id;
        const matchesStatus = !formik.values.filterStatus || formik.values.filterStatus.id === '' || job.status === formik.values.filterStatus.id;
        return matchesSearch && matchesBranch && matchesStatus;
    }).sort((a, b) => {
        const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
        const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
        return dateB - dateA;
    });

    const StatusBadge = ({ status }) => {
        const styles = {
            'OPEN': { color: 'primary', label: 'Open', icon: 'bi-hourglass' },
            'IN_PROGRESS': { color: 'warning', label: 'In Progress', icon: 'bi-gear-wide-connected' },
        };
        const style = styles[status] || { color: 'secondary', label: status, icon: 'bi-circle' };

        return (
            <span className={`badge badge-light-${style.color} fs-7 fw-bold px-3 py-2 d-flex align-items-center`}>
                <i className={`bi ${style.icon} me-2`}></i>
                {style.label}
            </span>
        );
    };

    const JobTypeBadge = ({ type }) => {
        const types = {
            'FREE_SERVICE': 'Free Service',
            'PAID_SERVICE': 'Paid Service',
            'RUNNING_REPAIR': 'Running Repair',
            'ACCIDENTAL_REPAIR': 'Accidental Repair'
        };
        return <span className="text-gray-600 fw-bold fs-7">{types[type] || type}</span>;
    };

    const getTreatmentProgress = (treatments) => {
        if (!treatments || treatments.length === 0) return { completed: 0, total: 0, percent: 0 };

        const completed = treatments.filter(t => t.status === 'COMPLETE').length;
        const total = treatments.length;
        const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

        return { completed, total, percent };
    };

    const getJobValue = (treatments) => {
        if (!treatments) return 0;
        return treatments.reduce((sum, t) => sum + (t.customerPrice || 0), 0);
    };

    const isOverdue = (committedDate) => {
        if (!committedDate) return false;
        return new Date(committedDate) < new Date();
    };

    return (
        <div className="card shadow-sm" style={{ borderRadius: '12px', backgroundColor: isDarkMode ? '#1e1e2d' : '' }}>
            {/* Header with Filters */}
            <div className="card-header border-0 pt-6">
                <div className="card-title flex-column align-items-start">
                    <h3 className="fw-bolder fs-2 mb-1">
                        <i className="bi bi-list-task text-primary me-2"></i>
                        Active Jobs Across All Branches
                    </h3>
                    <span className="text-muted fw-bold fs-7">
                        {loading ? 'Loading...' : `${filteredJobs.length} active job${filteredJobs.length !== 1 ? 's' : ''}`}
                    </span>
                </div>
                <div className="card-toolbar">
                    <div className="d-flex flex-wrap gap-3">
                        {/* Search */}
                        <div className="position-relative">
                            <i className="bi bi-search position-absolute translate-middle-y top-50 ms-3 text-gray-500"></i>
                            <input
                                type="text"
                                className="form-control form-control-sm ps-10"
                                placeholder="Search vehicle/RO..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ minWidth: '200px' }}
                            />
                        </div>

                        {/* Branch Filter */}
                        <div style={{ width: 'auto', minWidth: '150px' }}>
                            <style>{`
                                .filter-select-wrapper .form-label,
                                .filter-select-wrapper .form-text {
                                    display: none !important;
                                }
                            `}</style>
                            <div className="filter-select-wrapper">
                                <AGCStaticSelect
                                    data={branchOptions}
                                    formik={formik}
                                    isMulti={false}
                                    name="Branch"
                                    toolTip="Filter by branch"
                                    value={formik.values.filterBranch}
                                    id="filterBranch"
                                    forUpdate={false}
                                    required={false}
                                    includeAllOption={true}
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div style={{ width: 'auto', minWidth: '150px' }}>
                            <div className="filter-select-wrapper">
                                <AGCStaticSelect
                                    data={statusOptions}
                                    formik={formik}
                                    isMulti={false}
                                    name="Status"
                                    toolTip="Filter by status"
                                    value={formik.values.filterStatus}
                                    id="filterStatus"
                                    forUpdate={false}
                                    required={false}
                                    includeAllOption={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Body */}
            <div className="card-body pt-3">
                {loading ? (
                    <div className="text-center py-10">
                        <span className="spinner-border spinner-border-lg text-primary"></span>
                        <p className="mt-3 text-gray-600">Loading active jobs...</p>
                    </div>
                ) : filteredJobs.length === 0 ? (
                    <div className="alert alert-light-primary d-flex align-items-center p-5 mb-0">
                        <i className="bi bi-info-circle-fill fs-2 text-primary me-3"></i>
                        <div className="text-primary fw-bold">
                            {searchTerm || (formik.values.filterBranch && formik.values.filterBranch.id !== '') || (formik.values.filterStatus && formik.values.filterStatus.id !== '')
                                ? 'No jobs match your filters.'
                                : 'No active jobs found.'}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Desktop/Tablet Table View */}
                        <div className="table-responsive d-none d-md-block">
                            <table className="table table-row-bordered table-row-gray-100 align-middle gs-3 gy-3">
                                <thead>
                                    <tr className="fw-bolder text-muted bg-light">
                                        <th className="min-w-150px ps-4">Vehicle / RO</th>
                                        <th className="min-w-100px">Status</th>
                                        <th className="min-w-150px">Branch</th>
                                        <th className="min-w-100px">Value</th>
                                        <th className="min-w-150px">Progress</th>
                                        <th className="min-w-150px">Advisor</th>
                                        <th className="min-w-120px">Supervisor</th>
                                        <th className="min-w-130px pe-4">Committed Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredJobs.map(job => {
                                        const progress = getTreatmentProgress(job.selectedTreatments);
                                        const value = getJobValue(job.selectedTreatments);
                                        const overdue = isOverdue(job.committedDate);

                                        return (
                                            <tr
                                                key={job.id}
                                                className="cursor-pointer hover-elevate-up"
                                                style={{ transition: 'all 0.2s ease' }}
                                                onClick={() => onJobClick && onJobClick(job)}
                                            >
                                                <td className="ps-4">
                                                    <div className="d-flex flex-column">
                                                        <div className="d-flex align-items-center mb-1">
                                                            <i className="bi bi-car-front-fill text-primary fs-5 me-2"></i>
                                                            <span className="text-gray-900 fw-bolder fs-6">
                                                                {job.vehicleNo}
                                                            </span>
                                                            {overdue && job.status !== 'CLOSED' && (
                                                                <span className="badge badge-light-danger fs-9 fw-bold ms-2">
                                                                    Overdue
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-gray-600 fs-8">
                                                            RO: {job.roNumber}
                                                        </span>
                                                        <span className="text-gray-500 fs-9">
                                                            {job.jobType?.replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <StatusBadge status={job.status} />
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="text-gray-900 fw-bold fs-7">
                                                            {job.branchName || 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-gray-900 fw-bold fs-7">
                                                        ₹{value.toLocaleString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                            <span className="text-gray-700 fw-bold fs-8">
                                                                {progress.completed}/{progress.total}
                                                            </span>
                                                            <span className="text-muted fs-9 fw-bold ms-2">
                                                                {progress.percent}%
                                                            </span>
                                                        </div>
                                                        <div className="progress h-6px w-100 bg-gray-200">
                                                            <div
                                                                className="progress-bar bg-success"
                                                                style={{width: `${progress.percent}%`}}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-gray-700 fw-semibold fs-7">
                                                        {job.advisorName || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-gray-700 fw-semibold fs-7">
                                                        {job.supervisorName && job.supervisorName.length > 0
                                                            ? (Array.isArray(job.supervisorName) ? job.supervisorName.join(', ') : job.supervisorName)
                                                            : 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="pe-4">
                                                    <span className={`fw-bold fs-7 ${overdue ? 'text-danger' : 'text-gray-700'}`}>
                                                        {job.committedDate ? new Date(job.committedDate).toLocaleString([], {
                                                            day: 'numeric',
                                                            month: 'short',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        }) : 'N/A'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View - Lightweight */}
                        <div className="d-md-none row g-2">
                            {filteredJobs.map(job => {
                                const progress = getTreatmentProgress(job.selectedTreatments);
                                const overdue = isOverdue(job.committedDate);

                                return (
                                    <div key={job.id} className="col-6">
                                        <div
                                            className="card border border-gray-300 shadow-sm cursor-pointer h-100"
                                            onClick={() => onJobClick && onJobClick(job)}
                                            style={{
                                                backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <div className="card-body p-3">
                                                {/* Vehicle Number */}
                                                <div className="d-flex align-items-center mb-2">
                                                    {job.oemName ? (
                                                        <img
                                                            src={`/media/car/${job.oemName}.png`}
                                                            alt={job.oemName}
                                                            className="w-20px me-2"
                                                            onError={(e) => {e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline-block';}}
                                                        />
                                                    ) : null}
                                                    <i className="bi bi-car-front-fill text-primary fs-5 me-2" style={{display: job.oemName ? 'none' : 'inline-block'}}></i>
                                                    <span className="text-gray-900 fw-bolder fs-6">{job.vehicleNo}</span>
                                                </div>

                                                {/* Vehicle Model */}
                                                <div className="mb-2">
                                                    <span className="text-gray-600 fs-8 fw-semibold d-block">
                                                        {job.vehicleModelName || 'Model N/A'}
                                                    </span>
                                                </div>

                                                {/* Branch */}
                                                <div className="mb-2">
                                                    <span className="text-gray-700 fs-8 fw-bold">
                                                        <i className="bi bi-building text-success fs-8 me-1"></i>
                                                        {job.branchName || 'N/A'}
                                                    </span>
                                                </div>

                                                {/* Status Badges */}
                                                <div className="d-flex flex-wrap gap-1 mb-2">
                                                    {/* Status Badge - Mobile Compact Version */}
                                                    {job.status === 'OPEN' && (
                                                        <span className="badge badge-light-primary fs-9 fw-bold">
                                                            <i className="bi bi-hourglass me-1"></i>Open
                                                        </span>
                                                    )}
                                                    {job.status === 'IN_PROGRESS' && (
                                                        <span className="badge badge-light-warning fs-9 fw-bold">
                                                            <i className="bi bi-gear-wide-connected me-1"></i>In Progress
                                                        </span>
                                                    )}
                                                    {overdue && job.status !== 'CLOSED' && (
                                                        <span className="badge badge-light-danger fs-9 fw-bold">Overdue</span>
                                                    )}
                                                </div>

                                                {/* Treatment Progress */}
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="text-gray-600 fs-9 fw-bold">Treatment Progress</span>
                                                        <span className="text-primary fw-bold fs-8">{progress.percent}%</span>
                                                    </div>
                                                    <div className="progress h-4px w-100 bg-gray-300">
                                                        <div
                                                            className="progress-bar bg-primary"
                                                            style={{ width: `${progress.percent}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="mt-1 text-gray-500 fs-9 text-end">
                                                        {progress.completed} of {progress.total} completed
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
